import { CircularProgress, Stack, Typography } from '@mui/material'
import { WorkspaceMember, WorkspaceMemberProfile } from 'models/user/userWorkspaceDTOs'

import { AddMembersPopup } from 'components/pages/Client/WorkspacesProfile/AddMembersPopup'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { ChangeMemberRolePopup } from 'components/pages/Client/WorkspacesProfile/ChangeMemberRolePopup'
import { FC } from 'react'
import { GRAY_900 } from 'constants/styling/theme'
import { KeyboardEventKey } from 'constants/misc'
import { MUIButton } from 'components/common/MUIButton'
import { MembersTable } from './MembersTable.component'
import { PageContent } from 'components/common/Page/PageContent'
import { RemoveMemberWithTransferPopup } from 'components/pages/Client/WorkspacesProfile/RemoveMemberWithTransferPopup'
import { useMembersContentContext } from './MembersContent.context'
import { useTranslation } from 'react-i18next'
import { useWorkspaceRoles } from 'utils/hooks'

const toWorkspaceMember = (member: WorkspaceMemberProfile): WorkspaceMember => ({
  membershipId: member.membershipId,
  userId: member.userId,
  name: member.name,
  email: member.email,
  numberOfOrders: member.numberOfOrders,
  role: member.role,
  canOrder: member.canOrder,
  seatType: member.seatType,
})

export const MembersContent: FC = () => {
  const { t } = useTranslation(['profile'])

  const {
    workspaceId,
    isLoading,
    isWorkspacePersonal,
    sortedRows,
    isAddMembersOpen,
    setIsAddMembersOpen,
    changeMemberRole,
    setChangeMemberRole,
    removeMember,
    setRemoveMember,
  } = useMembersContentContext()

  const { isWorkspaceAdmin } = useWorkspaceRoles()

  return (
    <PageContent>
      <Stack padding="4rem 0" gap={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 550,
              color: GRAY_900,
              lineHeight: 1.5,
            }}
          >
            {t('profile:members_content.title_members', { count: sortedRows.length })}
          </Typography>

          {isWorkspaceAdmin &&
            <MUIButton type="defaultPrimary" size="md" startIcon={<AddRoundedIcon />} onClick={() => setIsAddMembersOpen(true)}>
              {t('profile:members_content.invite_member')}
            </MUIButton>
          }
        </Stack>

        {/* Table */}
        {isLoading
          ? <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '20rem' }}>
            <CircularProgress />
          </Stack>
          : <MembersTable />
        }
      </Stack>

      <AddMembersPopup isOpen={isAddMembersOpen} workspaceId={workspaceId} isWorkspacePersonal={isWorkspacePersonal} onClickOutside={() => setIsAddMembersOpen(false)} onClickClose={() => setIsAddMembersOpen(false)} />

      {!!removeMember && <RemoveMemberWithTransferPopup isOpen={!!removeMember} workspaceId={workspaceId} member={toWorkspaceMember(removeMember)} onClickOutsideOrClose={() => setRemoveMember(undefined)} onKeyDown={(e) => e.key === KeyboardEventKey.ESCAPE && setRemoveMember(undefined)} callbackAfterSuccessfulResponse={() => setRemoveMember(undefined)} />}

      {!!changeMemberRole?.memberId && <ChangeMemberRolePopup isOpen={!!changeMemberRole} workspaceId={workspaceId} changeMemberRole={changeMemberRole} onClickOutsideOrClose={() => setChangeMemberRole(null)} onKeyDown={(e) => e.key === KeyboardEventKey.ESCAPE && setChangeMemberRole(null)} callbackAfterSuccessfulResponse={() => setChangeMemberRole(null)} />}

    </PageContent>
  )
}
